import { useEffect, useState, useRef } from 'react'

import { Box, Button, Flex, Image, Show, Skeleton, SkeletonText, Text, useDisclosure } from '@chakra-ui/react'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import { useNavigate } from 'react-router-dom'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { PostData } from '@core/app/types/postsApiType'
import { IconArrowBackPost } from '@core/assets/icons/arrow_back_post'
import { PlayButtonIcon } from '@core/assets/icons/play_button'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'

import { ImageGallery } from '@page/post/components/PostInfo/ImageGallery/ImageGallery'

import 'swiper/css'

const baseClass = 'PostInfo'
const postPageClass = 'PostPageNew'

interface Props {
    data: PostData | undefined
    loaded: boolean
    isPostExpired: boolean
    isPostArchived?: boolean
}

const setCollapseTextToLocalStorage = (value: boolean) => {
    localStorage.setItem('PostInfo_collapseText', JSON.stringify(value))
}

const getCollapseTextFromLocalStorage = () => {
    return JSON.parse(localStorage.getItem('PostInfo_collapseText') || 'false')
}

export const PostInfoBlock = ({ data, loaded, isPostExpired, isPostArchived }: Props): JSX.Element => {
    const { isOpen, onClose, onOpen } = useDisclosure()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [vimeoVideo, setVimeoVideo] = useState<string>('')
    const customPrefix: string = document.getElementById('__custom_route_data')?.getAttribute('data-prefix') ?? ''

    const [collapseText, setCollapseText] = useState<boolean>(getCollapseTextFromLocalStorage())
    const [isDescriptionLarge, setIsDescriptionLarge] = useState<boolean>(false)
    const [shouldShowButton, setShouldShowButton] = useState<boolean>(false)
    const descriptionRef = useRef<HTMLDivElement>(null)
    const headerDescRef = useRef<HTMLDivElement>(null)
    const searchString = localStorage.getItem('jobs_search_string')

    const getFontStyles = (size: number) => {
        switch (true) {
            case size > 0 && size <= 200:
                return {
                    fontSize: '24px',
                    lineHeight: '36px',
                }
            case size > 200 && size <= 400:
                return {
                    fontSize: '20px',
                    lineHeight: '32px',
                }
            case size > 400 && size <= 600:
                return {
                    fontSize: '18px',
                    lineHeight: '28px',
                }
            case size > 600:
                return {
                    fontSize: '16px',
                    lineHeight: '24px',
                }
        }
    }

    useEffect(() => {
        const isVimeo = data?.ad?.VideoUrl && data?.ad?.VideoUrl?.includes('vimeo')
        if (isVimeo) {
            const videoId = data?.ad?.VideoUrl?.split('/').pop()
            setVimeoVideo(`https://player.vimeo.com/video/${videoId}`) // TODO - what for this?
        }
        setIsDescriptionLarge(data?.ad?.Body && data?.ad.Body.split(' ').length < 75 ? false : true)

        const content = document.getElementsByClassName('content')[0]
        if (content) {
            const links = content.getElementsByTagName('a')
            // @ts-ignore
            for (const link of links) {
                link.setAttribute('target', '_blank')
                link.setAttribute('rel', 'noopener noreferrer')
                link.style.color = '#007BFF'
                link.style.textDecoration = 'underline'
            }
        }
    }, [data])

    useEffect(() => {
        setCollapseTextToLocalStorage(collapseText)
    }, [shouldShowButton, collapseText])

    useEffect(() => {
        const descHeight = descriptionRef?.current?.scrollHeight || 0
        const headDescHeight = headerDescRef?.current?.scrollHeight || 0
        const maxAllowedHeight = 800
        setShouldShowButton(descHeight + headDescHeight > maxAllowedHeight)
    }, [data, loaded])

    // const [videoIframeUrl, setVideoIframeUrl] = useState<string>('')
    //
    // useEffect(() => {
    //     if (data?.ad?.VideoUrl && data.ad.VideoUrl.includes('youtube.com')) {
    //         const urlParams = new URLSearchParams(new URL(data.ad.VideoUrl).search)
    //         const videoId = urlParams.get('v')
    //         if (videoId) {
    //             setVideoIframeUrl(`https://www.youtube.com/embed/${videoId}`)
    //         }
    //     } else if (data?.ad?.VideoUrl && data.ad.VideoUrl.includes('vimeo.com')) {
    //         const videoId = data.ad.VideoUrl.split('/').pop()
    //         if (videoId) {
    //             setVideoIframeUrl(`https://player.vimeo.com/video/${videoId}`)
    //         }
    //     }
    // }, [data])

    return (
        <>
            {isOpen && data && (
                <ImageGallery isOpen={isOpen} onClose={onClose} images={data?.images} companyName={data?.ad?.Name} />
            )}
            <Box
                w={'full'}
                bg={'bg.1'}
                borderRadius={'8px'}
                overflow={'hidden'}
                boxShadow={'0px 1px 6px 0px rgba(0,0,0,0.15)'}
                className={`${postPageClass} ${baseClass}`}
            >
                <Flex gap={4} px={{ base: 4, xl: 6 }} py={5} alignItems={'baseline'}>
                    <Button
                        variant={'link'}
                        onClick={() =>
                            (location.href = `${window.location.origin}${customPrefix}/jobs?${searchString}`)
                        }
                        _hover={{
                            backgroundColor: '#F5F5F5',
                        }}
                        aria-label="Back"
                        minHeight={'40px'}
                    >
                        <Box
                            sx={{
                                transition: 'transform 0.3s, fill 0.3s',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                                '&:hover path': {
                                    fill: '#686868',
                                },
                            }}
                        >
                            <IconArrowBackPost w={'20'} h={'14'} />
                        </Box>
                    </Button>

                    <Skeleton isLoaded={loaded} w={'full'} minH={'30px'} alignSelf={'start'} pt={'4px'}>
                        <Flex gap={'16px'} justifyContent={'center'}>
                            <Text
                                as={'h5'}
                                fontSize={'xl'}
                                fontWeight={'semibold'}
                                color={'text.main'}
                                style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                                // opacity={isPostExpired ? 0.4 : 1}
                                textAlign={'center'}
                                paddingRight={'60px'}
                                // lineHeight={'1'}
                            >
                                {decodeHtmlEntitiesRecursive(data?.ad?.Name || '')}
                            </Text>
                            {isPostExpired && (
                                <Text
                                    fontSize={'14px'}
                                    color={'#904616'}
                                    backgroundColor={'#FEF1E9'}
                                    px={'10px'}
                                    display={'flex'}
                                    alignItems={'center'}
                                    borderRadius={'15px'}
                                    fontWeight={'500'}
                                >
                                    {isPostArchived ? t('3836', 'Archived') : t('1219', 'Expired')}
                                </Text>
                            )}
                        </Flex>
                    </Skeleton>
                </Flex>
                {data?.ad?.AdLogo && (
                    <Skeleton isLoaded={loaded} w={'full'} maxH={{ base: '185px', xl: '300px' }}>
                        <Box
                            w={'full'}
                            maxH={{ base: '185px', xl: '300px' }}
                            // px={6}
                            px={{ base: 6, xl: 12 }}
                            display={'flex'}
                            justifyContent={'center'}
                            className={`${baseClass}--logo-wrap`}
                        >
                            <Image
                                src={data?.ad?.AdLogo}
                                objectFit={'scale-down'}
                                alt={data?.ad?.Name}
                                // filter={isPostExpired ? 'saturate(0) opacity(0.6)' : 'none'}
                                className={`${baseClass}--logo-wrap-img`}
                            />
                        </Box>
                    </Skeleton>
                )}
                <Flex
                    flexDirection={'column'}
                    py={{ base: 5, xl: 5 }}
                    px={{ base: 5, xl: 12 }}
                    gap={{ base: 6, xl: 10 }}
                >
                    {loaded ? (
                        <>
                            {data?.ad?.JobTitle && (
                                <Box
                                    pb={{ base: '32px', xl: '40px' }}
                                    borderBottom={'1px solid'}
                                    borderColor={'border.3'}
                                >
                                    <Text
                                        fontSize={getFontStyles(data?.ad?.JobTitle?.length)?.fontSize}
                                        color={'text.main'}
                                        lineHeight={getFontStyles(data?.ad?.JobTitle?.length)?.lineHeight}
                                        // lineHeight={'30px'}
                                        fontWeight={'semibold'}
                                        // opacity={isPostExpired ? 0.4 : 1}
                                    >
                                        {decodeHtmlEntitiesRecursive(data?.ad?.JobTitle)}
                                    </Text>
                                </Box>
                            )}

                            {data?.ad.Header && !data?.showCompanyBottom && (
                                <Box
                                    ref={headerDescRef}
                                    className={'PostInfo-header-desc'}
                                    pb={{ base: '32px', xl: '40px' }}
                                    borderBottom={'1px solid'}
                                    borderColor={'border.3'}
                                >
                                    {data?.ad.EmpName && (
                                        <Text
                                            fontSize={'md'}
                                            fontWeight={'semibold'}
                                            color={'text.main'}
                                            lineHeight={'20px'}
                                            mb={4}
                                            // opacity={isPostExpired ? 0.4 : 1}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.EmpName || '')}
                                        </Text>
                                    )}

                                    <Text
                                        className={`header-ul-ol`}
                                        whiteSpace={'pre-line'}
                                        wordBreak={'break-word'}
                                        fontSize={'sm'}
                                        color={'text.secondary'}
                                        // lineHeight={'8px'}
                                        dangerouslySetInnerHTML={{ __html: data?.ad?.Header || '' }}
                                        // opacity={isPostExpired ? 0.4 : 1}
                                    />
                                </Box>
                            )}

                            <Box
                                className={`content ${shouldShowButton && !collapseText ? 'collapsed' : ''} PostInfo-content`}
                                position={'relative'}
                                overflow={'hidden'}
                                maxH={shouldShowButton && !collapseText ? '450px' : 'auto'}
                                ref={descriptionRef}
                                // opacity={isPostExpired ? 0.4 : 1}
                            >
                                {data?.ad?.Body && parse(data?.ad?.Body)}
                            </Box>
                            {shouldShowButton && (
                                <Button
                                    w={'auto'}
                                    mx={'auto'}
                                    _hover={{
                                        backgroundColor: '#E8E8E8',
                                    }}
                                    leftIcon={
                                        <Box style={{ transform: !collapseText ? 'none' : 'rotate(180deg)' }}>
                                            <svg viewBox={`0 0 8 2`} width={'8px'} height={'8px'} fill="none">
                                                <path
                                                    d="M1 0.5L4 3.5L7 0.5"
                                                    stroke={'#4f4f4f'}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </Box>
                                    }
                                    variant={'outline'}
                                    onClick={() => setCollapseText((prev) => !prev)}
                                    color={'text.main'}
                                    borderColor={'#E8E8E8'}
                                    fontWeight={'medium'}
                                    fontSize={'sm'}
                                >
                                    {!collapseText ? t('6810') : t('7423')}
                                </Button>
                            )}

                            {data?.ad.Header && data?.showCompanyBottom && (
                                <Box
                                    ref={headerDescRef}
                                    className={'content PostInfo-header-desc'}
                                    pt={{ base: '22px', xl: '30px' }}
                                    // pb={{ base: '22px', xl: '30px' }}
                                    borderTop={'1px solid'}
                                    borderColor={'border.3'}
                                >
                                    {data?.ad.EmpName && (
                                        <Text
                                            //changed to xl (2xl before) for botom because of feedback from Lars (remove comment if everything fine with this implementation)
                                            // fontSize={'md'}
                                            fontWeight={'semibold'}
                                            color={'text.main'}
                                            // lineHeight={'36px'}
                                            mb={4}
                                            // opacity={isPostExpired ? 0.4 : 1}
                                            lineHeight={'20px'}
                                            as={'h6'}
                                            fontSize={'14px'}
                                        >
                                            {decodeHtmlEntitiesRecursive(data?.ad?.EmpName || '')}
                                            <span>&nbsp;</span>
                                        </Text>
                                    )}
                                    <Text
                                        // whiteSpace={'pre-line'}
                                        wordBreak={'break-word'}
                                        fontSize={'sm'}
                                        color={'text.secondary'}
                                        lineHeight={'24px'}
                                        dangerouslySetInnerHTML={{ __html: data?.ad?.Header || '' }}
                                        // opacity={isPostExpired ? 0.4 : 1}
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <SkeletonText noOfLines={4} spacing={'4'} skeletonHeight={'2'} />
                    )}

                    <Flex flexDirection={'column'} w={'full'} gap={0}>
                        <Skeleton isLoaded={loaded} w={'full'} hidden={data?.keywords.length === 0}>
                            <Flex
                                alignItems={{ base: 'start', xl: 'center' }}
                                gap={2}
                                py={2}
                                borderTop={'1px solid'}
                                borderColor={'border.1'}
                                flexDirection={{ base: 'column', xl: 'row' }}
                            >
                                <Text
                                    minW={'160px'}
                                    fontSize={'sm'}
                                    color={'text.secondary'}
                                    // opacity={isPostExpired ? 0.4 : 1}
                                >
                                    {t('343')}
                                </Text>
                                <Flex gap={2} flexWrap={'wrap'}>
                                    {data?.keywords.map((keyword: any) => (
                                        <Box
                                            as={'div'}
                                            bg={'formElements.tagDefault'}
                                            px={3}
                                            py={1.5}
                                            borderRadius={'3px'}
                                            key={keyword['Keyword']}
                                        >
                                            <Text
                                                fontSize={'sm'}
                                                color={'text.secondary'}
                                                // opacity={isPostExpired ? 0.4 : 1}
                                            >
                                                {decodeHtmlEntitiesRecursive(keyword['Keyword'])}
                                            </Text>
                                        </Box>
                                    ))}
                                </Flex>
                            </Flex>
                        </Skeleton>
                        <Skeleton isLoaded={loaded} w={'full'}>
                            {!!(data?.ad?.BranchCategoryID && data?.ad?.Branch) && (
                                <Flex
                                    alignItems={{ base: 'start', xl: 'center' }}
                                    gap={2}
                                    py={2}
                                    borderTop={'1px solid'}
                                    borderColor={'border.1'}
                                    flexDirection={{ base: 'column', xl: 'row' }}
                                >
                                    <Text
                                        minW={'160px'}
                                        fontSize={'sm'}
                                        color={'text.secondary'}
                                        // opacity={isPostExpired ? 0.4 : 1}
                                    >
                                        {t('189')}
                                    </Text>
                                    <Flex gap={2} flexWrap={'wrap'}>
                                        {data?.ad.Branch && (
                                            <Flex>
                                                <Box
                                                    bg={'#C5DDFF'}
                                                    position={'relative'}
                                                    px={3}
                                                    py={1.5}
                                                    borderRadius={'3px'}
                                                >
                                                    <Text
                                                        fontSize={'sm'}
                                                        color={'text.secondary'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                    >
                                                        {decodeHtmlEntitiesRecursive(data?.ad.Branch)}
                                                    </Text>
                                                </Box>
                                                {data?.ad.BranchCategory && (
                                                    <Box
                                                        ml={'-3px'}
                                                        bg={'#E1EEFF'}
                                                        px={3}
                                                        py={1.5}
                                                        borderRadius={'3px'}
                                                    >
                                                        <Text
                                                            fontSize={'sm'}
                                                            color={'text.secondary'}
                                                            // opacity={isPostExpired ? 0.4 : 1}
                                                        >
                                                            {decodeHtmlEntitiesRecursive(data?.ad.BranchCategory)}
                                                        </Text>
                                                    </Box>
                                                )}
                                            </Flex>
                                        )}
                                        {data?.ad?.SecondBranch && data?.ad?.BranchID !== data?.ad.SecondBranchID && (
                                            // data?.ad.BranchCategoryID !== data?.ad.SecondBranchCategoryID &&
                                            <Flex>
                                                <Box
                                                    bg={'#C5DDFF'}
                                                    position={'relative'}
                                                    px={3}
                                                    py={1.5}
                                                    borderRadius={'3px'}
                                                >
                                                    <Text
                                                        fontSize={'sm'}
                                                        color={'text.secondary'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                    >
                                                        {decodeHtmlEntitiesRecursive(data?.ad.SecondBranch)}
                                                    </Text>
                                                </Box>
                                                {data?.ad?.SecondaryBranchCategory && (
                                                    <Box
                                                        ml={'-3px'}
                                                        bg={'#E1EEFF'}
                                                        px={3}
                                                        py={1.5}
                                                        borderRadius={'3px'}
                                                    >
                                                        <Text
                                                            fontSize={'sm'}
                                                            color={'text.secondary'}
                                                            // opacity={isPostExpired ? 0.4 : 1}
                                                        >
                                                            {decodeHtmlEntitiesRecursive(
                                                                data?.ad.SecondaryBranchCategory
                                                            )}
                                                        </Text>
                                                    </Box>
                                                )}
                                            </Flex>
                                        )}
                                    </Flex>
                                </Flex>
                            )}
                        </Skeleton>
                    </Flex>
                </Flex>
            </Box>
            {data?.images && data?.images.length > 1 ? (
                <Box as={'div'} w={'full'} mt={'8'} position={'relative'}>
                    <Box
                        as={'div'}
                        className={'swiper-button image-swiper-button-next'}
                        position={'absolute'}
                        top={'50%'}
                        right={{ lg: '0', xl: '-24px' }}
                        transform={'translateY(-50%)'}
                        zIndex={'10'}
                        display={{ base: 'none', xl: 'block' }}
                    >
                        <Box
                            as={'div'}
                            w={'12'}
                            h={'12'}
                            bg={'bg.1'}
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            borderRadius={'full'}
                            boxShadow={'0px 8px 12px rgba(0, 0, 0, 0.08)'}
                            cursor={'pointer'}
                            transform={'rotate(180deg)'}
                            _hover={{
                                backgroundColor: '#F5F5F5',
                            }}
                        >
                            <IconArrowBackPost w={'20'} h={'14'} fill={'#828282'} />
                        </Box>
                    </Box>
                    <Box
                        as={'div'}
                        className={'swiper-button image-swiper-button-prev'}
                        position={'absolute'}
                        top={'50%'}
                        left={{ lg: '0', xl: '-24px' }}
                        transform={'translateY(-50%)'}
                        zIndex={'10'}
                        display={{ base: 'none', xl: 'block' }}
                    >
                        <Box
                            as={'div'}
                            w={'12'}
                            h={'12'}
                            bg={'bg.1'}
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            borderRadius={'full'}
                            boxShadow={'0px 8px 12px rgba(0, 0, 0, 0.08)'}
                            cursor={'pointer'}
                            _hover={{
                                backgroundColor: '#F5F5F5',
                            }}
                        >
                            <IconArrowBackPost w={'20'} h={'14'} fill={'#828282'} />
                        </Box>
                    </Box>
                    <Swiper
                        slidesPerView={2.5}
                        spaceBetween={8}
                        pagination={{
                            clickable: true,
                        }}
                        navigation={{
                            nextEl: '.image-swiper-button-next',
                            prevEl: '.image-swiper-button-prev',
                            disabledClass: 'swiper-button-disabled',
                        }}
                        modules={[Navigation]}
                        className={'post-images'}
                        autoHeight={true}
                        breakpoints={{
                            0: {
                                slidesPerView: 1.5,
                            },
                            650: {
                                slidesPerView: 2.5,
                            },
                        }}
                    >
                        {data?.images.map((image: any) => (
                            <SwiperSlide key={data?.images.indexOf(image)}>
                                <Box
                                    as={'div'}
                                    onClick={onOpen}
                                    transition={'all 0.3s ease'}
                                    _hover={{
                                        transform: 'scale(1.02)',
                                        border: '1px solid lightgrey',
                                    }}
                                >
                                    <Image
                                        src={image['Image']}
                                        minW={{ base: '180px', md: '315px' }}
                                        maxHeight={'450px'}
                                        h={'450px'}
                                        objectFit={'cover'}
                                        style={{ cursor: 'pointer' }}
                                        // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                    />
                                </Box>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            ) : (
                <Box onClick={onOpen} mt={'8'} hidden={data?.images?.length === 0}>
                    {data?.images?.length == 1 && (
                        <Box as="div" mt="8" onClick={onOpen}>
                            <Box
                                position="relative"
                                w="full"
                                overflow="hidden"
                                borderRadius="8px"
                                // height динамічно від ширини: співвідношення 3:2
                                sx={{ aspectRatio: '3 / 2' }}
                                // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                            >
                                <Box
                                    position="absolute"
                                    inset={0}
                                    bgImage={`url(${data?.images[0]['Image']})`}
                                    bgRepeat="no-repeat"
                                    bgSize="cover"
                                    bgPosition="center"
                                    transform="scale(1.06)"
                                    filter="blur(14px)"
                                />

                                <Image
                                    src={data?.images[0]['Image']}
                                    alt={data?.ad?.Name}
                                    position="relative"
                                    zIndex={1}
                                    w="100%"
                                    h="100%"
                                    objectFit="contain"
                                    bg="rgba(0,0,0,0.02)"
                                    style={{ cursor: 'pointer' }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
            {data?.ad.VideoUrl && (
                <Box as={'div'} mt={8} className={`${postPageClass}__video ${baseClass}__video`}>
                    <Box
                        as="div"
                        mt={8}
                        // filter={isPostExpired ? 'saturate(0)' : 'none'}
                        // pointerEvents={isPostExpired ? 'none' : 'auto'}
                        position="relative"
                        paddingTop={'56.25%'}
                    >
                        <ReactPlayer
                            url={data?.ad.VideoUrl}
                            style={{ position: 'absolute', top: 0, left: 0 }}
                            width={'100%'}
                            height={'100%'}
                            playIcon={<PlayButtonIcon />}
                            controls={true}
                            config={{
                                vimeo: {
                                    playerOptions: {
                                        responsive: true,
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            )}
        </>
    )
}
